import { supabase, setMobileSession } from '../lib/supabase';

export const otpService = {
  async generateOTP(mobileNumber) {
    // Set mobile session before operations
    await setMobileSession(mobileNumber);
    
    // Call Edge Function to send OTP via MSG91
    const { data, error } = await supabase.functions.invoke('send-otp', {
      body: {
        mobileNumber: mobileNumber,
        action: 'send'
      }
    });

    if (error) {
      console.error('Error calling send-otp function:', error);
      throw new Error(error.message || 'Failed to send OTP. Please try again.');
    }

    if (!data || !data.success) {
      throw new Error(data?.error || 'Failed to send OTP. Please try again.');
    }

    return {
      id: data.otpId,
      requestId: data.requestId,
      message: data.message,
      expiresAt: new Date(Date.now() + 5 * 60 * 1000).toISOString() // 5 minutes (MSG91 expiry)
    };
  },

  async verifyOTP(mobileNumber, otpCode) {
    // Set mobile session before operations
    await setMobileSession(mobileNumber);
    
    try {
      // Call Edge Function to verify OTP via MSG91
      const { data, error } = await supabase.functions.invoke('send-otp', {
        body: {
          mobileNumber: mobileNumber,
          action: 'verify',
          otp: otpCode
        }
      });

      // Handle Edge Function invocation errors (network, non-2xx, etc.)
      if (error) {
        console.error('Error calling send-otp function:', error);
        
        // Extract meaningful error message
        let errorMessage = 'Failed to verify OTP. Please try again.';
        
        // Handle specific error types
        if (error.message) {
          // Check if it's a technical error that should be user-friendly
          if (error.message.includes('Edge Function') || error.message.includes('non-2xx')) {
            errorMessage = 'Verification service error. Please try again or request a new OTP.';
          } else if (error.message.includes('Invalid OTP') || error.message.includes('OTP')) {
            errorMessage = error.message; // Keep OTP-related errors as-is
          } else {
            errorMessage = 'Invalid OTP. Please check and try again.';
          }
        } else if (error.status) {
          // Handle specific status codes
          if (error.status === 400) {
            errorMessage = 'Invalid OTP. Please check and try again.';
          } else if (error.status === 500) {
            errorMessage = 'Verification service error. Please try again later.';
          } else if (error.status === 401 || error.status === 403) {
            errorMessage = 'Authentication error. Please try again.';
          }
        }
        
        throw new Error(errorMessage);
      }

      // Handle response data
      if (!data) {
        throw new Error('No response from verification service. Please try again.');
      }

      // Check verification status
      if (!data.verified && !data.success) {
        // Extract error message from response
        const errorMessage = data.error || 'Invalid OTP. Please try again.';
        throw new Error(errorMessage);
      }

      return {
        verified: true,
        mobileNumber: mobileNumber,
        message: data.message || 'OTP verified successfully'
      };
    } catch (error) {
      // Re-throw with better error message if needed
      if (error.message) {
        throw error; // Already has a good error message
      }
      throw new Error('Failed to verify OTP. Please try again.');
    }
  },

  async resendOTP(mobileNumber) {
    // Set mobile session before operations
    await setMobileSession(mobileNumber);
    
    // Invalidate any existing unverified OTPs
    await supabase?.from('otp_verifications')?.update({ is_verified: false })?.eq('mobile_number', mobileNumber)?.eq('is_verified', false);

    // Generate new OTP
    return this.generateOTP(mobileNumber);
  }
};